import React, { useState, useEffect, useRef } from 'react';
import { Layout } from './shared';
import { Users, Target, Award, BookOpen } from 'lucide-react';

const AboutPage = ({ openRequestModal }) => {
  const [isPageLoaded, setIsPageLoaded] = useState(false);
  const [visibleSections, setVisibleSections] = useState(new Set());
  const sectionRefs = useRef({});

  // Page load animation
  useEffect(() => {
    const timer = setTimeout(() => setIsPageLoaded(true), 100);
    return () => clearTimeout(timer);
  }, []);

  // Intersection Observer for scroll animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections((prev) => new Set([...prev, entry.target.id]));
          }
        });
      },
      { threshold: 0.1, rootMargin: '50px' }
    );

    Object.values(sectionRefs.current).forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, []);
  return (
    <div className={`transition-opacity duration-1000 ease-out ${
      isPageLoaded ? 'opacity-100' : 'opacity-0'
    }`}>
      <Layout openRequestModal={openRequestModal}>
        {/* Hero Section */}
        <section 
          id="hero" 
          ref={(el) => (sectionRefs.current.hero = el)}
          className="px-6 py-20 bg-gradient-to-br from-white via-blue-50 to-sky-200"
        >
          <div className={`max-w-4xl mx-auto text-center transition-all duration-1000 ease-out ${
            visibleSections.has('hero') || isPageLoaded 
              ? 'opacity-100 translate-y-0' 
              : 'opacity-0 translate-y-8'
          }`}>
            <h1 className={`text-4xl md:text-5xl font-bold text-gray-900 mb-6 transition-all duration-1200 ease-out delay-200 ${
              visibleSections.has('hero') || isPageLoaded 
                ? 'opacity-100 translate-y-0' 
                : 'opacity-0 translate-y-4'
            }`}>
              Built by researchers, <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">for researchers</span>
            </h1>
            <p className={`text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto transition-all duration-1200 ease-out delay-400 ${
              visibleSections.has('hero') || isPageLoaded 
                ? 'opacity-100 translate-y-0' 
                : 'opacity-0 translate-y-4'
            }`}>
              DigiTables.ai was born from the frustration of spending months digitizing documents instead of analyzing them. We've solved this problem at scale.
            </p>
          </div>
        </section>

      {/* Our Story Section */}
      <section 
        id="story" 
        ref={(el) => (sectionRefs.current.story = el)}
        className="px-6 py-20 bg-white"
      >
        <div className="max-w-4xl mx-auto">
          <div className={`text-center mb-16 transition-all duration-1000 ease-out ${
            visibleSections.has('story') 
              ? 'opacity-100 translate-y-0' 
              : 'opacity-0 translate-y-8'
          }`}>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Our story</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            </p>
          </div>

          <div className="prose prose-lg max-w-2xl mx-auto text-gray-600">
            <p className="text-xl leading-relaxed mb-8">
              <strong className="text-gray-900">DigiTables.ai was built by researchers, for researchers.</strong> Our proprietary table digitization process fuses scalable OCR technology with blazing-fast human review.
            </p>
            
            <p className="text-lg leading-relaxed mb-8">
              We deliver accurate, analysis-ready data at speed and scale—without burdening your in-house staff. We understand the pain of manual data entry, inconsistent and slow results from vendors, and error-prone OCR pipelines because we've lived it.
            </p>

            <p className="text-lg leading-relaxed">
              DigiTables.ai overcomes the limitations of other solutions to turn unstructured tables into structured datasets that researchers can trust, allowing teams to focus on insights rather than cleaning data.
            </p>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="px-6 py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">What drives us</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Our values are shaped by our experience as researchers who needed better tools.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-lg transition-shadow">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mr-4">
                  <Target className="text-blue-600" size={24} />
                </div>
                <h3 className="text-xl font-semibold text-gray-900">Research-grade typical accuracy</h3>
              </div>
              <p className="text-gray-600">
                We maintain 99.9% typical accuracy because we know that research depends on reliable data. Every dataset goes through our human-in-the-loop validation process.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-lg transition-shadow">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mr-4">
                  <Users className="text-green-600" size={24} />
                </div>
                <h3 className="text-xl font-semibold text-gray-900">Researcher-first design</h3>
              </div>
              <p className="text-gray-600">
                Built by people who understand research workflows. We design for the way researchers actually work, not how we think they should work.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-lg transition-shadow">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mr-4">
                  <BookOpen className="text-purple-600" size={24} />
                </div>
                <h3 className="text-xl font-semibold text-gray-900">Proven at scale</h3>
              </div>
              <p className="text-gray-600">
                Over 100,000 pages processed across historical tabular data, regulatory filings, and semi-structured text. Our system has been battle-tested on real projects.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-lg transition-shadow">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center mr-4">
                  <Award className="text-orange-600" size={24} />
                </div>
                <h3 className="text-xl font-semibold text-gray-900">Speed without compromise</h3>
              </div>
              <p className="text-gray-600">
                Fast turnaround times without sacrificing quality. We understand research deadlines and deliver analysis-ready data when you need it.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section 
        id="mission" 
        ref={(el) => (sectionRefs.current.mission = el)}
        className="px-6 py-20 bg-gray-50"
      >
        <div className="max-w-4xl mx-auto text-center">
          <div className={`transition-all duration-1000 ease-out ${
            visibleSections.has('mission') 
              ? 'opacity-100 translate-y-0' 
              : 'opacity-0 translate-y-8'
          }`}>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12">Our mission</h2>
            
            {/* Enhanced Mission Statement */}
            <div className="relative">
              <div className="bg-white rounded-3xl p-12 shadow-xl border border-gray-100 relative overflow-hidden">
                {/* Background decoration */}
                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-full -translate-y-16 translate-x-16 opacity-50"></div>
                <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-purple-100 to-blue-100 rounded-full translate-y-12 -translate-x-12 opacity-50"></div>
                
                <p className="text-xl md:text-2xl text-gray-800 leading-relaxed font-medium relative z-10">
                  To eliminate the time researchers spend on manual data entry and error-prone table extraction, so they can focus on what matters most:
                  <span className="text-blue-600 font-semibold"> generating insights that drive research and business decisions.</span>
                </p>
              </div>
            </div>
            
            
            <div className="mt-12">
              <button 
                onClick={openRequestModal}
                className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl font-semibold hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                Get in touch
              </button>
            </div>
          </div>
        </div>
      </section>
      </Layout>
    </div>
  );
};

export default AboutPage;


